import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, QrCode, Store } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { useOrder } from "@/store/orderStore";
import AllergyPicker from "@/components/AllergyPicker";
import { PrivacyNote } from "@/components/PrivacyNote";
import TenantHeader from "@/components/TenantHeader";
import { EVENTS, track } from "@/lib/analytics";
import { qrErrorMessage } from "@/lib/serverMessages";

// `value` is what the order carries; the label is looked up per language.
const FULFILLMENTS = [
  { value: "dine_in", labelKey: "guestEntry.dineIn" },
  { value: "takeaway", labelKey: "guestEntry.takeaway" },
];

const GuestEntry = ({ onContinue, qrError, onDismissQrError }) => {
  const { state, setGuest } = useOrder();
  const { user } = useAuth();
  const { t } = useTranslation();
  const g = state.guest;
  const [touched, setTouched] = useState(false);

  const takeaway = g.fulfillment === "takeaway";
  // A takeaway order is collected at the counter; there is no table to name.
  const valid = takeaway || String(g.table).trim().length > 0;

  // Someone who signed in has already told us their name; asking again is a
  // field they have to fill in twice to get the same result.
  useEffect(() => {
    if (user?.name && !String(g.name || "").trim()) setGuest({ name: user.name });
    // Only ever fills a blank — a name typed by hand is never overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name]);

  // Arrived by scanning the table's QR code: the table is settled, not asked for.
  const unlockTable = () => setGuest({ tableLocked: false, tableToken: null, table: "" });

  return (
    <div className="px-5 pt-8 pb-10">
      <TenantHeader />

      <h1 className="text-[40px] leading-[1.05] font-black tracking-tight text-neutral-900 mb-2">
        {t("guestEntry.title")}
      </h1>
      <p className="text-base text-neutral-600 mb-8 max-w-[30ch]">
        {g.tableLocked ? t("guestEntry.subtitleReady") : t("guestEntry.subtitleSetup")}
      </p>

      {qrError && (
        <div
          data-testid="qr-table-error"
          className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="flex-1 text-sm text-amber-900">{qrErrorMessage(t, qrError)}</div>
          <button
            data-testid="qr-table-error-dismiss"
            onClick={onDismissQrError}
            className="text-xs font-bold uppercase tracking-wider text-amber-700"
          >
            {t("common.ok")}
          </button>
        </div>
      )}

      <div className="space-y-4">
        <Card label={t("guestEntry.howEating")}>
          <div className="grid grid-cols-2 gap-2">
            {FULFILLMENTS.map((opt) => (
              <Choice
                key={opt.value}
                testid={`fulfillment-${opt.value}`}
                active={g.fulfillment === opt.value}
                onClick={() => setGuest({ fulfillment: opt.value })}
              >
                {t(opt.labelKey)}
              </Choice>
            ))}
          </div>
        </Card>

        {/* Takeaway is collected at the counter, so it names no table — not even
            a scanned one. Saying "collect at Table 1" contradicted the way the
            order is actually handed over, and "Not yours?" then cleared a card
            that had nothing to put in its place: the table field is not rendered
            for takeaway, so the guest was left with neither a table nor a way to
            name one. The scan itself is kept, because it proves the guest is on
            the premises even when the table number is no longer used. */}
        {takeaway ? (
          <div
            data-testid="takeaway-counter"
            className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-neutral-900 text-white">
              <Store size={18} />
            </div>
            <div className="text-base font-bold leading-tight text-neutral-900">
              {t("guestEntry.collectCounter")}
            </div>
          </div>
        ) : g.tableLocked ? (
          <div
            data-testid="qr-table-confirmed"
            className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white px-4 py-3"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-neutral-900 text-white">
              <QrCode size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">
                {t("guestEntry.yourTable")}
              </div>
              <div className="text-lg font-black leading-tight text-neutral-900">
                {t("common.table", { number: g.table })}
              </div>
            </div>
            <button
              data-testid="qr-table-change"
              onClick={unlockTable}
              className="text-xs font-bold uppercase tracking-wider text-[#E34A27] text-right shrink-0"
            >
              {t("guestEntry.notYours")}
            </button>
          </div>
        ) : (
          <Field
            label={t("guestEntry.tableLabel")}
            testid="input-table"
            value={g.table}
            inputMode="numeric"
            placeholder={t("guestEntry.tablePlaceholder")}
            onChange={(v) => setGuest({ table: v })}
            error={touched && !valid ? t("common.required") : null}
          />
        )}

        {/* amp-block: the guest's name and what they cannot eat. A session
            recording masks text everywhere, but these two are not ours to
            record at all — see lib/analytics.js and /privacy.

            Open on every path, including after a scan. These used to fold away
            behind one tap so that a scanned arrival was a single tap to order,
            and the allergy field is exactly the wrong thing to fold: a guest who
            never opens it never declares, and this screen is the only place most
            of them ever could. One tap saved is not worth a declaration lost. */}
        <div className="amp-block space-y-4">
          <Field
            label={t("common.name")}
            testid="input-name"
            value={g.name}
            placeholder={t("common.optional")}
            onChange={(v) => setGuest({ name: v })}
          />
          <AllergyPicker value={g.allergy} onChange={(v) => setGuest({ allergy: v })} />
        </div>

        {/* A plate or a board is a choice with two answers, and it was drawn as
            one chip that was either on or off. "Serve on a board" unlit is not
            visibly "on a plate" — the default was real (`served: "plate"`) and
            invisible, so the guest could not see what they had, only what they
            could switch to. Skipping seasoning genuinely is a yes/no, so it gets
            a switch and a line of its own rather than sitting in the same row
            pretending to be the third serving style. */}
        <Card label={t("guestEntry.serving")}>
          <div className="grid grid-cols-2 gap-2">
            <Choice
              testid="pref-plate"
              active={g.served !== "board"}
              onClick={() => setGuest({ served: "plate" })}
            >
              {t("guestEntry.prefPlate")}
            </Choice>
            <Choice
              testid="pref-board"
              active={g.served === "board"}
              onClick={() => setGuest({ served: "board" })}
            >
              {t("guestEntry.prefBoard")}
            </Choice>
          </div>

          <button
            data-testid="pref-no-seasoning"
            onClick={() => setGuest({ noSeasoning: !g.noSeasoning })}
            aria-pressed={!!g.noSeasoning}
            className="mt-3 flex w-full items-center justify-between gap-3 border-t border-neutral-100 px-1 pt-3 text-left text-sm font-bold text-neutral-900"
          >
            {t("guestEntry.noSeasoning")}
            <span
              aria-hidden="true"
              className={`relative h-6 w-10 shrink-0 rounded-full transition-colors ${
                g.noSeasoning ? "bg-neutral-900" : "bg-neutral-200"
              }`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                  g.noSeasoning ? "left-[18px]" : "left-0.5"
                }`}
              />
            </span>
          </button>
        </Card>
      </div>

      <button
        data-testid="start-order-btn"
        onClick={() => {
          setTouched(true);
          if (!valid) return;
          track(EVENTS.ORDER_STARTED, {
            fulfillment: g.fulfillment,
            table_scanned: Boolean(g.tableLocked),
            named: Boolean(String(g.name || "").trim()),
            // How many, never which: an allergy is medical information and does
            // not leave for a third party. The count is what says whether this
            // screen is being used at all.
            allergies_declared: String(g.allergy || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean).length,
            served: g.served === "board" ? "board" : "plate",
            no_seasoning: Boolean(g.noSeasoning),
          });
          onContinue();
        }}
        className="mt-8 w-full h-16 rounded-2xl bg-[#E34A27] text-white font-black text-lg tracking-wide active:scale-[0.98] transition-transform"
      >
        {t("guestEntry.startOrder")}
      </button>
      <PrivacyNote className="mt-6" />
    </div>
  );
};

/** A titled white card. Two questions on this screen wear the same shell. */
const Card = ({ label, children }) => (
  <div className="bg-white border border-neutral-200 rounded-2xl p-3">
    <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 px-1">
      {label}
    </div>
    {children}
  </div>
);

/** One answer to a two-answer question: how you are eating, how it is served. */
const Choice = ({ testid, active, onClick, children }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    aria-pressed={active}
    className={`h-11 rounded-xl text-sm font-bold transition-colors ${
      active ? "bg-neutral-900 text-white" : "bg-neutral-50 text-neutral-700"
    }`}
  >
    {children}
  </button>
);

const Field = ({ label, testid, value, onChange, placeholder, inputMode, error }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 block px-1">
      {label}
    </label>
    <input
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      className={`w-full h-14 bg-white border ${
        error ? "border-red-400" : "border-neutral-200"
      } rounded-2xl px-4 text-base font-medium text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-900 transition-colors`}
    />
    {error && <p className="text-xs text-red-500 mt-1 px-1">{error}</p>}
  </div>
);

export default GuestEntry;
